import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';

const initialState = {
  status: 'idle',
  error: null,
};

// Async thunks
export const fetchItems = createAsyncThunk(
  'items/fetchItems',
  async ({ namespace, params }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.getItems(namespace, params);
  },
);

export const fetchItem = createAsyncThunk(
  'items/fetchItem',
  async ({ namespace, itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.getItem(namespace, itemId);
  },
);

// Kept out of the store: the URL-level extras belong to the panel that asked
// for them, not to the item.
export const fetchItemExtra = createAsyncThunk(
  'items/fetchItemExtra',
  async ({ namespace, itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.getItemExtra(namespace, itemId);
  },
);

// Kept out of the store, like the extras it reads.
export const enhanceUnfetchedItemUrls = createAsyncThunk(
  'items/enhanceUnfetchedItemUrls',
  async ({ namespace, itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.enhanceUnfetchedItemUrls(namespace, itemId);
  },
);

// Kept out of the store. Suggestions belong to one editing session, not to the
// item.
export const fetchItemSuggestions = createAsyncThunk(
  'items/fetchItemSuggestions',
  async ({ namespace, itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.suggestItemMetadata(namespace, itemId);
  },
);

// Fetch several items by id (e.g. related items) without touching the current
// view context. Missing/forbidden items are dropped.
export const fetchItemsByIds = createAsyncThunk(
  'items/fetchItemsByIds',
  async ({ namespace, ids }, { extra }) => {
    const { itemsApi } = extra;
    const results = await Promise.all(
      ids.map((id) => itemsApi.getItem(namespace, id).catch(() => null)),
    );
    return results.filter(Boolean);
  },
);

// Fetch a page of an author's items (e.g. the author hero page preview)
// without touching the current view context.
export const fetchItemsByAuthorPreview = createAsyncThunk(
  'items/fetchItemsByAuthorPreview',
  async ({ namespace, authorId, params }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.getItemsByAuthor(namespace, authorId, params);
  },
);

// Fetch a page of a list's items (e.g. the list hero page preview). A distinct
// action type keeps contextSlice from reacting and clobbering the current view.
export const fetchItemsByListPreview = createAsyncThunk(
  'items/fetchItemsByListPreview',
  async ({ namespace, listId, params }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.getItems(namespace, { ...params, listId });
  },
);

// Fetch a page of a site's items (e.g. the site hero page preview). A distinct
// action type keeps contextSlice from reacting and clobbering the current view.
export const fetchItemsBySitePreview = createAsyncThunk(
  'items/fetchItemsBySitePreview',
  async ({ namespace, siteId, params }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.getItemsBySite(namespace, siteId, params);
  },
);

export const createItem = createAsyncThunk(
  'items/createItem',
  async ({ namespace, item, listId }, { extra, rejectWithValue }) => {
    const { itemsApi } = extra;
    try {
      return await itemsApi.createItem(namespace, item, listId);
    } catch (error) {
      if (error.data) {
        return rejectWithValue(error.data);
      }
      throw error;
    }
  },
);

export const bookmarkItem = createAsyncThunk(
  'items/bookmarkItem',
  async ({ itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.bookmarkItem(itemId);
  },
);

export const updateItem = createAsyncThunk(
  'items/updateItem',
  async ({ namespace, itemId, patch, reviewed }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.updateItem(namespace, itemId, patch, { reviewed });
  },
);

export const deleteItem = createAsyncThunk(
  'items/deleteItem',
  async ({ namespace, itemId }, { extra }) => {
    const { itemsApi } = extra;
    await itemsApi.deleteItem(namespace, itemId);
    return itemId;
  },
);

export const batchUpdateItems = createAsyncThunk(
  'items/batchUpdate',
  async ({ namespace, ids, patch }, { extra }) => {
    const { itemsApi } = extra;
    await itemsApi.batchUpdate(namespace, ids, patch);
    return ids;
  },
);

export const batchDeleteItems = createAsyncThunk(
  'items/batchDelete',
  async ({ namespace, ids }, { extra }) => {
    const { itemsApi } = extra;
    await itemsApi.batchDelete(namespace, ids);
    return ids;
  },
);

export const importItems = createAsyncThunk(
  'items/importItems',
  async ({ urls }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.batchImport(urls);
  },
);

export const mergeItems = createAsyncThunk(
  'items/mergeItems',
  async ({ namespace, targetItemId, sourceItemIds }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.mergeItems(namespace, targetItemId, sourceItemIds);
  },
);

export const cloneItem = createAsyncThunk(
  'items/cloneItem',
  async ({ namespace, itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.cloneItem(namespace, itemId);
  },
);

export const shareItem = createAsyncThunk(
  'items/shareItem',
  async ({ itemId }, { extra }) => {
    const { itemsApi } = extra;
    return itemsApi.shareItem(itemId);
  },
);

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItems.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.error || action.error.message;
      })
      .addCase(bookmarkItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(bookmarkItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(bookmarkItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(batchUpdateItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(batchUpdateItems.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(batchUpdateItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(batchDeleteItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(batchDeleteItems.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(batchDeleteItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(importItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(importItems.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(importItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(mergeItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(mergeItems.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(mergeItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(cloneItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(cloneItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(cloneItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(shareItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(shareItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(shareItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export default itemsSlice.reducer;
